import { EVIDENCE_TYPES } from '@paguz/contracts';
import type {
  ApplicationStatus,
  BlockReason,
  CreditLineStatus,
  DrawdownStatus,
  EvidenceDocumentStatus,
  EvidenceType,
  FundingMode,
  InstallmentStatus,
  KnockOutRule,
  PaymentIntentStatus,
  Recommendation,
  ReviewAlert,
  SagaStep,
  SagaStepStatus,
} from '@paguz/contracts';
import type { BadgeProps } from '@paguz/ui';
import type { ExternalSystem } from '../api/admin-schemas.ts';

/** Etiquetas de UI (es-CO) para enums de `@paguz/contracts` (glosario `02-glosario.md`). */
type Tone = NonNullable<BadgeProps['variant']>;

export const PAYMENT_STATUS: Readonly<Record<PaymentIntentStatus, { label: string; tone: Tone }>> =
  {
    CREATED: { label: 'Creada', tone: 'secondary' },
    AWAITING_SCA: { label: 'Esperando confirmación', tone: 'secondary' },
    VALIDATING: { label: 'Validando', tone: 'highlight' },
    CREDIT_AUTHORIZED: { label: 'Cupo autorizado', tone: 'highlight' },
    FUNDING: { label: 'Fondeando', tone: 'highlight' },
    FUNDED: { label: 'Fondeada', tone: 'highlight' },
    SENT_TO_BREB: { label: 'Enviada a Bre-B', tone: 'highlight' },
    PENDING_CONFIRMATION: { label: 'En verificación', tone: 'warning' },
    COMPENSATING: { label: 'Compensando', tone: 'warning' },
    COMPLETED: { label: 'Completada', tone: 'success' },
    FAILED: { label: 'Fallida', tone: 'danger' },
    CANCELLED: { label: 'Cancelada', tone: 'outline' },
    EXPIRED: { label: 'Vencida', tone: 'outline' },
  };

export const SAGA_STEP: Readonly<Record<SagaStep, { label: string; description: string }>> = {
  VALIDATE: { label: 'Validación del medio de pago', description: 'Cliente, línea y beneficiario' },
  LIMITS: { label: 'Límites transaccionales', description: 'Reglas M8: monto, diario, velocidad' },
  AUTHORIZE: { label: 'Autorización de crédito', description: 'Hold de cupo en Credit Core' },
  FUND: { label: 'Fondeo en cuenta vinculada', description: 'Crédito e inmovilización (JIT)' },
  SEND: { label: 'Envío Bre-B', description: 'Orden de transferencia inmediata' },
  AWAIT_CONFIRMATION: { label: 'Confirmación Bre-B', description: 'Callback o consulta de estado' },
  CAPTURE: { label: 'Captura y posteo', description: 'Utilización y plan de cuotas' },
  DEBIT: { label: 'Débito de la inmovilización', description: 'Cuenta vinculada (JIT)' },
  RELEASE: { label: 'Liberación del cupo', description: 'Compensación del hold' },
  REVERSE_FUNDING: { label: 'Reverso del fondeo', description: 'Compensación en la cuenta' },
};

export const STEP_STATUS: Readonly<Record<SagaStepStatus, { label: string; tone: Tone }>> = {
  STARTED: { label: 'En curso', tone: 'highlight' },
  SUCCEEDED: { label: 'Exitoso', tone: 'success' },
  FAILED: { label: 'Fallido', tone: 'danger' },
  UNKNOWN: { label: 'Sin respuesta', tone: 'warning' },
  COMPENSATED: { label: 'Compensado', tone: 'secondary' },
};

export const SYSTEM_LABEL: Readonly<Record<ExternalSystem, string>> = {
  PAGUZ: 'Paguz',
  CREDIT_CORE: 'Credit Core',
  DEPOSIT_CORE: 'Core de depósitos',
  BREB: 'Bre-B',
  NOTIFICATIONS: 'Notificaciones',
};

export const FUNDING_MODE: Readonly<Record<FundingMode, { label: string; description: string }>> = {
  SETTLEMENT_ACCOUNT: {
    label: 'Cuenta puente',
    description:
      'La entidad paga desde su cuenta puente. No toca la cuenta del cliente: menos pasos y sin GMF al cliente.',
  },
  LINKED_ACCOUNT_JIT: {
    label: 'Cuenta vinculada (justo a tiempo)',
    description:
      'Se acredita e inmoviliza el monto en la cuenta del cliente y se debita al confirmar Bre-B. Agrega pasos de fondeo y reverso.',
  },
};

export const LINE_STATUS: Readonly<Record<CreditLineStatus, { label: string; tone: Tone }>> = {
  PENDING_ACTIVATION: { label: 'Por activar', tone: 'secondary' },
  ACTIVE: { label: 'Activa', tone: 'success' },
  BLOCKED: { label: 'Bloqueada', tone: 'danger' },
  DELINQUENT: { label: 'En mora', tone: 'warning' },
  CLOSED: { label: 'Cerrada', tone: 'outline' },
};

export const BLOCK_REASON: Readonly<Record<BlockReason, string>> = {
  CUSTOMER_REQUEST: 'Solicitud del cliente',
  FRAUD: 'Sospecha de fraude',
  RISK: 'Riesgo crediticio',
  DELINQUENCY: 'Mora',
  ADMIN: 'Decisión administrativa',
};

export const DRAWDOWN_STATUS: Readonly<Record<DrawdownStatus, { label: string; tone: Tone }>> = {
  ACTIVE: { label: 'Normal', tone: 'success' },
  IN_DISPUTE: { label: 'En disputa', tone: 'warning' },
  PARTIALLY_REVERSED: { label: 'Reversada parcial', tone: 'secondary' },
  REVERSED: { label: 'Reversada', tone: 'outline' },
  PAID_OFF: { label: 'Pagada', tone: 'secondary' },
  CHARGED_OFF: { label: 'Castigada', tone: 'danger' },
};

export const INSTALLMENT_STATUS: Readonly<
  Record<InstallmentStatus, { label: string; tone: Tone }>
> = {
  SCHEDULED: { label: 'Programada', tone: 'outline' },
  BILLED: { label: 'Facturada', tone: 'highlight' },
  PARTIALLY_PAID: { label: 'Pago parcial', tone: 'warning' },
  PAID: { label: 'Pagada', tone: 'success' },
  OVERDUE: { label: 'Vencida', tone: 'danger' },
  REVERSED: { label: 'Reversada', tone: 'secondary' },
};

export const KNOCK_OUT: Readonly<Record<KnockOutRule, string>> = {
  SANCTIONS_LIST_MATCH: 'Coincidencia en listas restrictivas',
  BUREAU_CURRENT_DELINQUENCY_GT_30: 'Mora vigente > 30 días en central',
  UNDERAGE: 'Menor de edad',
  OVER_MAX_AGE: 'Supera la edad máxima',
  RECENT_REJECTION: 'Rechazo reciente',
  IDENTITY_NOT_VERIFIED: 'Identidad no verificada',
};

export const REVIEW_ALERT: Readonly<Record<ReviewAlert, string>> = {
  DOCUMENT_FRAUD_SIGNAL: 'Señal de alteración en documentos',
  DOCUMENT_NEEDS_VALIDATION: 'Documento por validar',
  SANCTIONS_REVIEW: 'Posible coincidencia en listas',
  SALES_TRIANGULATION_DIVERGENCE: 'Divergencia en la triangulación de ventas',
  OUT_OF_COVERAGE: 'Fuera de zona de cobertura',
  BUREAU_THIN_FILE: 'Poca historia en central',
  INCOME_INCONSISTENCY: 'Ingresos inconsistentes',
};

export const CHANNEL_LABEL: Readonly<Record<string, string>> = {
  APP: 'App',
  WIDGET: 'Widget',
  AGENT: 'Asesor IA',
  API: 'API',
};

export const PHASE_LABEL: Readonly<Record<string, string>> = {
  DISCOVERY: 'Descubrimiento',
  CONSENT: 'Consentimientos',
  IDENTITY: 'Identidad',
  BUSINESS_PROFILE: 'Perfil del negocio',
  EVIDENCE: 'Evidencias',
  CASH_FLOW: 'Flujo de caja',
  OFFER_SIMULATION: 'Simulación de oferta',
  SUBMITTED: 'Enviada',
  DECISION: 'En decisión',
  FORMALIZATION: 'Formalización',
  ACTIVE: 'Activa',
};

// ── Originación (T-CON-06) ────────────────────────────────────────────────
export const EVIDENCE_LABEL: Readonly<Record<EvidenceType, string>> = EVIDENCE_TYPES.labels;

export const APPLICATION_STATUS: Readonly<
  Record<ApplicationStatus, { label: string; tone: Tone }>
> = {
  IN_PROGRESS: { label: 'En curso', tone: 'secondary' },
  SUBMITTED: { label: 'Por decidir', tone: 'highlight' },
  APPROVED: { label: 'Aprobada', tone: 'success' },
  REJECTED: { label: 'Rechazada', tone: 'danger' },
  COUNTER_OFFERED: { label: 'Contraoferta', tone: 'warning' },
  WITHDRAWN: { label: 'Desistida', tone: 'outline' },
  ABANDONED: { label: 'Abandonada', tone: 'outline' },
  ACTIVE: { label: 'Cupo activo', tone: 'success' },
};

export const RECOMMENDATION: Readonly<Record<Recommendation, { label: string; tone: Tone }>> = {
  APPROVE: { label: 'Aprobar', tone: 'success' },
  REJECT: { label: 'Rechazar', tone: 'danger' },
  REVIEW: { label: 'Revisar', tone: 'warning' },
  COUNTER_OFFER: { label: 'Contraoferta', tone: 'warning' },
};

export const DOCUMENT_STATUS: Readonly<
  Record<EvidenceDocumentStatus, { label: string; tone: Tone }>
> = {
  UPLOADED: { label: 'Cargado', tone: 'secondary' },
  ANALYZING: { label: 'Por revisar', tone: 'warning' },
  ACCEPTED: { label: 'Aceptado', tone: 'success' },
  NEEDS_REPLACEMENT: { label: 'Reemplazar', tone: 'warning' },
  REJECTED: { label: 'Rechazado', tone: 'danger' },
  SUPERSEDED: { label: 'Reemplazado', tone: 'outline' },
};

export const CASH_FLOW_METHOD: Readonly<Record<string, string>> = {
  DECLARED: 'Declarado por el cliente',
  DOCUMENT: 'Documento',
  TRIANGULATED: 'Triangulado',
  ESTIMATED: 'Estimado',
};

export const CONSENT_LABEL: Readonly<Record<string, string>> = {
  HABEAS_DATA: 'Tratamiento de datos (habeas data)',
  BUREAU_QUERY: 'Consulta en centrales de riesgo',
  TERMS: 'Términos y condiciones',
  AI_PROCESSING: 'Atención con asistente de IA',
  ELECTRONIC_COMMS: 'Comunicaciones electrónicas',
};
